import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Menu,
  MenuItem,
  ClickAwayListener,
  Fade,
  makeStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import ActionButton from '../actionButton';
import AlertDialog from '../alertDialog';
import InputField from '../inputField';
import CustomRichTextEditor from '../customRichTextEditor';
import ReorderItems from '../reorderItems';
import Scrollbars from '../scrollbars';
import SurveyPreview from '../surveyPreview';
import Tooltip from '../tooltip';
import Question from './question';
import { DELAY } from './question/questionnaireQuestionEntry/config';
import { ReactComponent as QuestionnaireIcon } from '../../../assets/icons/live_help.svg';
import { ReactComponent as PreviewIcon } from '../../../assets/icons/fact_check_light.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/warning-triangle.svg';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import {
  EMPTY_QUESTIONNAIRE,
  QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT,
  QUESTIONNAIRE_QUESTION_TYPES,
} from '../../../constants/survey';
import {
  reorder,
  replaceObjectInList,
  removeObjectFromArray,
  asyncDebounce,
  isObjectEmpty,
} from '../../../utility/helpers';
import {
  createQuestionnaireQuestion,
  duplicateQuestionnaireQuestion,
  addNewQuestionnaireQuestionOption,
  prepareQuestionnaireQuestionOptions,
} from '../../../utility/survey';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 8,
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tabsWrapper: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    borderRadius: '8px 8px 0 0',
    justifyContent: 'space-between',
    display: 'flex',
    marginBottom: spacing(4.5),
    padding: spacing(5, 5, 0, 5),
    borderBottom: `1px solid ${primary.bluish5}`,
  },
  tabWrapper: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    cursor: 'pointer',
  },
  selectedTabWrapper: {
    borderBottom: `4px solid ${primary.bluish1}`,
    paddingBottom: spacing(3.75),
  },
  buttonWrapper: {
    height: spacing(6),
  },
  tabIcon: {
    width: 24,
    height: 24,
    paddingRight: spacing(2),
  },
  tabError: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: spacing(1.5),
  },
  questionsView: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  questionsWrapper: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    borderRadius: '0 0 8px 8px',
    padding: spacing(0, 5),
    flex: 1,
    minHeight: 0,
  },
  questionsScrollY: {
    right: 7,
  },
  fieldWrapper: {
    marginBottom: spacing(6),
  },
  descriptionFieldWrapper: {
    marginBottom: spacing(8),
  },
  textEditor: {
    '& .ql-toolbar': {
      borderTopLeftRadius: 3,
      borderTopRightRadius: 3,
    },
    '& .ql-container': {
      height: 188,
      borderBottomLeftRadius: 3,
      borderBottomRightRadius: 3,
      '& .ql-blank::before': {
        lineHeight: '24px',
      },
    },
  },
  error: {
    marginBottom: spacing(1),
  },
  descriptionError: {
    bottom: -20,
  },
  questionItem: {
    marginBottom: spacing(6),
  },
  addButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
    padding: spacing(4, 0),
  },
  questionsRequiredBanner: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    height: 44,
    borderRadius: 8,
    backgroundColor: primary.red5,
  },
  questionsRequiredIcon: {
    flexShrink: 0,
    width: 20,
    height: 18,
    marginLeft: spacing(4),
    marginRight: spacing(4),
  },
  questionsRequiredText: {
    lineHeight: '20px',
  },
  addIconButton: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: primary.white,
    padding: 0,
    '&:hover': {
      backgroundColor: primary.bluish9,
    },
  },
  addIcon: {
    width: 24,
    height: 24,
  },
  menuPaper: {
    borderRadius: 0,
    boxShadow: 'none',
    border: `1px solid ${primary.bluish8}`,
  },
  menuItem: {
    boxSizing: 'border-box',
    borderBottom: `1px solid ${primary.bluish8}`,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2, 4),
    minWidth: 220,
    '&:last-of-type': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  menuItemIcon: {
    flexShrink: 0,
    width: 16,
    height: 16,
    marginRight: spacing(2),
    marginTop: spacing(-0.5),
  },
  multipleSelectIcon: {
    width: 14,
    height: 14,
    marginRight: spacing(2.25),
    marginLeft: spacing(0.25),
  },
  previewWrapper: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

const VIEW_QUESTIONS = 'questions';
const VIEW_PREVIEW = 'preview';

const getQuestionErrors = (questionsError, index) => {
  if (!questionsError || typeof questionsError !== 'object') {
    return {};
  }

  return questionsError[index] ?? {};
};

const QuestionnaireQuestionsField = ({
  translations,
  values,
  questionnaire = EMPTY_QUESTIONNAIRE,
  hasError = {},
  onChange,
  onValidate = undefined,
  onRegisterFlush = undefined,
}) => {
  const classes = useStyles();
  const {
    name = '',
    description = '',
    questions = [],
  } = questionnaire ?? EMPTY_QUESTIONNAIRE;
  const [nameText, setNameText] = useState(name);
  const [descriptionText, setDescriptionText] = useState(description);
  const [activeView, setActiveView] = useState(VIEW_QUESTIONS);
  const [anchorEl, setAnchorEl] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [questionToDuplicate, setQuestionToDuplicate] = useState(null);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [isDuplicateDialogOpened, setIsDuplicateDialogOpened] = useState(false);
  const isAddingQuestionRef = useRef(false);
  const nameRef = useRef(name);
  const descriptionRef = useRef(description);
  const questionsRef = useRef(questions);
  const onValidateRef = useRef(onValidate);
  const entryFlushCallbacksRef = useRef(new Set());

  onValidateRef.current = onValidate;

  const questionOptions = prepareQuestionnaireQuestionOptions(
    translations.questionOptions
  );

  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  useEffect(() => {
    if (questionnaire?.name !== nameRef.current) {
      nameRef.current = questionnaire?.name ?? '';
      setNameText(questionnaire?.name ?? '');
    }
  }, [questionnaire?.name]);

  useEffect(() => {
    if (questionnaire?.description !== descriptionRef.current) {
      descriptionRef.current = questionnaire?.description ?? '';
      setDescriptionText(questionnaire?.description ?? '');
    }
  }, [questionnaire?.description]);

  const updateQuestionnaire = useCallback(
    updatedQuestionnaire => onChange(updatedQuestionnaire),
    [onChange]
  );

  const savePayloadRef = useRef({
    updateQuestionnaire,
  });

  savePayloadRef.current = {
    updateQuestionnaire,
  };

  const handleNameSaveRef = useRef(null);
  if (!handleNameSaveRef.current) {
    handleNameSaveRef.current = asyncDebounce(async () => {
      const { updateQuestionnaire: saveQuestionnaire } = savePayloadRef.current;

      await saveQuestionnaire({
        name: nameRef.current,
        description: descriptionRef.current,
        questions: questionsRef.current,
      });
    }, DELAY);
  }

  const debouncedQuestionnaireSave = handleNameSaveRef.current;

  const getPendingQuestionnaire = useCallback(
    () => ({
      name: nameRef.current,
      description: descriptionRef.current,
      questions: questionsRef.current,
    }),
    []
  );

  const validatePendingQuestionnaire = useCallback(() => {
    onValidateRef.current?.(getPendingQuestionnaire());
  }, [getPendingQuestionnaire]);

  const registerEntryFlush = useCallback(flushFn => {
    entryFlushCallbacksRef.current.add(flushFn);

    return () => entryFlushCallbacksRef.current.delete(flushFn);
  }, []);

  useEffect(() => {
    if (!onRegisterFlush) {
      return undefined;
    }

    const flushAll = () =>
      Promise.all([
        handleNameSaveRef.current.flush(),
        ...Array.from(entryFlushCallbacksRef.current).map(flush => flush()),
      ]);

    onRegisterFlush(flushAll);

    return () => onRegisterFlush(null);
  }, [onRegisterFlush]);

  const updateQuestions = useCallback(
    updatedQuestions => {
      questionsRef.current = updatedQuestions;
      updateQuestionnaire({
        name: nameRef.current,
        description: descriptionRef.current,
        questions: updatedQuestions,
      });
      validatePendingQuestionnaire();
    },
    [updateQuestionnaire, validatePendingQuestionnaire]
  );

  const handleNameChange = e => {
    const { value } = e.target;

    nameRef.current = value;
    setNameText(value);
    validatePendingQuestionnaire();
    debouncedQuestionnaireSave();
  };

  const handleDescriptionChange = value => {
    descriptionRef.current = value;
    setDescriptionText(value);
    validatePendingQuestionnaire();
    debouncedQuestionnaireSave();
  };

  const handlePendingQuestionChange = useCallback(
    updatedQuestion => {
      const index = questionsRef.current.findIndex(
        q => q.id === updatedQuestion.id
      );

      if (index === -1) {
        return;
      }

      questionsRef.current = replaceObjectInList(
        questionsRef.current,
        index,
        updatedQuestion
      );
      validatePendingQuestionnaire();
    },
    [validatePendingQuestionnaire]
  );

  const handleUpdateQuestion = updatedQuestion => {
    const currentQuestions = questionsRef.current;

    updateQuestions(
      replaceObjectInList(
        currentQuestions,
        currentQuestions.findIndex(q => q.id === updatedQuestion.id),
        updatedQuestion
      )
    );
  };

  const handleDragEnd = result => {
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const newOrder = reorder(
      questionsRef.current,
      result.source.index,
      result.destination.index
    );

    updateQuestions(
      newOrder.map((question, index) => ({
        ...question,
        order: index + 1,
      }))
    );
  };

  const handleAddQuestion = useCallback(
    type => {
      if (isAddingQuestionRef.current) return;

      isAddingQuestionRef.current = true;
      setAnchorEl(null);

      const currentQuestions = questionsRef.current;
      const newQuestion = createQuestionnaireQuestion(
        translations.placeholders.questionTitle,
        type,
        currentQuestions.length + 1,
        QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT
      );

      const updatedQuestions = [...currentQuestions, newQuestion];
      questionsRef.current = updatedQuestions;
      updateQuestions(updatedQuestions);
    },
    [translations.placeholders.questionTitle, updateQuestions]
  );

  const onAddQuestion = useCallback(
    type => () => {
      handleAddQuestion(type);
    },
    [handleAddQuestion]
  );

  const handleAddAnswer = question => value => {
    handleUpdateQuestion(addNewQuestionnaireQuestionOption(question, value));
  };

  const handleOpenDeleteQuestion = question => {
    setQuestionToDelete(question);
    setIsDeleteDialogOpened(true);
  };

  const handleCloseDeleteQuestion = () => {
    setIsDeleteDialogOpened(false);
    setQuestionToDelete(null);
  };

  const onDeleteQuestion = () => {
    const modifiedQuestionList = removeObjectFromArray(
      questionsRef.current,
      questionToDelete
    );

    updateQuestions(
      modifiedQuestionList.map((question, index) => ({
        ...question,
        order: index + 1,
      }))
    );
    setIsDeleteDialogOpened(false);
    setQuestionToDelete(null);
  };

  const handleOpenDuplicateQuestion = question => {
    setQuestionToDuplicate(question);
    setIsDuplicateDialogOpened(true);
  };

  const handleCloseDuplicateQuestion = () => {
    setIsDuplicateDialogOpened(false);
    setQuestionToDuplicate(null);
  };

  const onDuplicateQuestion = () => {
    const currentQuestions = questionsRef.current;

    updateQuestions([
      ...currentQuestions,
      duplicateQuestionnaireQuestion(
        questionToDuplicate,
        currentQuestions.length + 1
      ),
    ]);
    setIsDuplicateDialogOpened(false);
    setQuestionToDuplicate(null);
  };

  const handleOpenAddQuestionMenu = useCallback(event => {
    if (isAddingQuestionRef.current) return;
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseAddQuestionMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleAddQuestionMenuTransitionExited = useCallback(() => {
    isAddingQuestionRef.current = false;
  }, []);

  const handleSetActiveView = view => () => setActiveView(view);

  const nameHasError = !!hasError?.name;
  const descriptionHasError = !!hasError?.description;
  const nameErrorMessage = translations[hasError?.name];
  const descriptionErrorMessage = translations[hasError?.description];
  const hasQuestionnaireErrors = Boolean(hasError) && !isObjectEmpty(hasError);
  const hasRequiredQuestionsError = hasError?.questions === 'required';
  const hadQuestionnaireErrorsRef = useRef(hasQuestionnaireErrors);

  useEffect(() => {
    const errorsJustAppeared =
      hasQuestionnaireErrors && !hadQuestionnaireErrorsRef.current;

    if (errorsJustAppeared && activeView === VIEW_PREVIEW) {
      setActiveView(VIEW_QUESTIONS);
    }

    hadQuestionnaireErrorsRef.current = hasQuestionnaireErrors;
  }, [hasQuestionnaireErrors, activeView]);

  const renderQuestionsView = () => (
    <div className={classes.questionsView}>
      <Scrollbars
        className={classes.questionsWrapper}
        verticalScrollClass={classes.questionsScrollY}
      >
        <div
          className={classNames(classes.fieldWrapper, {
            [classes.error]: nameHasError,
          })}
        >
          <InputField
            label={translations.name.label}
            value={nameText}
            placeholder={translations.name.placeholder}
            error={nameHasError}
            errorMessage={nameErrorMessage}
            onChange={handleNameChange}
            fullWidth
          />
        </div>
        <div className={classes.descriptionFieldWrapper}>
          <CustomRichTextEditor
            customEditorClass={classes.textEditor}
            customLabelClass={classes.descriptionError}
            label={translations.description.label}
            placeholder={translations.description.placeholder}
            value={descriptionText}
            hasError={descriptionHasError}
            errorMessage={descriptionErrorMessage}
            onUpdateText={handleDescriptionChange}
          />
        </div>
        <ReorderItems onSave={handleDragEnd}>
          {questions.map((questionItem, index) => (
            <Question
              key={`question_${questionItem.id}`}
              id={questionItem.id}
              className={classes.questionItem}
              translations={translations}
              errors={getQuestionErrors(hasError?.questions, index)}
              question={questionItem}
              questionOptions={questionOptions}
              onDuplicate={handleOpenDuplicateQuestion}
              onDelete={handleOpenDeleteQuestion}
              onSave={handleUpdateQuestion}
              onPendingChange={handlePendingQuestionChange}
              onRegisterFlush={registerEntryFlush}
              onAddAnswer={handleAddAnswer(questionItem)}
            />
          ))}
        </ReorderItems>
        {hasRequiredQuestionsError && (
          <div className={classes.questionsRequiredBanner} role="alert">
            <ErrorIcon className={classes.questionsRequiredIcon} />
            <Typography
              className={classes.questionsRequiredText}
              variant="body2"
            >
              {translations.questionsRequired}
            </Typography>
          </div>
        )}
      </Scrollbars>
      <ClickAwayListener
        onClickAway={handleCloseAddQuestionMenu}
        mouseEvent="onMouseDown"
      >
        <div className={classes.addButtonWrapper}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.ADD}
            hasBorder
            tooltipText={translations.newQuestion}
            iconButtonClass={classes.addIconButton}
            iconClass={classes.addIcon}
            onClickHandler={handleOpenAddQuestionMenu}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseAddQuestionMenu}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            classes={{ paper: classes.menuPaper }}
            TransitionProps={{
              onExited: handleAddQuestionMenuTransitionExited,
            }}
          >
            {questionOptions.map(option => (
              <MenuItem
                key={option.id}
                className={classes.menuItem}
                onClick={onAddQuestion(option.id)}
              >
                <option.icon
                  className={classNames(classes.menuItemIcon, {
                    [classes.multipleSelectIcon]:
                      option.id === QUESTIONNAIRE_QUESTION_TYPES.MULTI_SELECT,
                  })}
                />
                <Typography variant="body2">{option.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </div>
      </ClickAwayListener>
    </div>
  );

  return (
    <div className={classes.main}>
      <div className={classes.content}>
        <div className={classes.tabsWrapper}>
          <div
            className={classNames(classes.tabWrapper, classes.buttonWrapper, {
              [classes.selectedTabWrapper]: activeView === VIEW_QUESTIONS,
            })}
            onClick={handleSetActiveView(VIEW_QUESTIONS)}
          >
            <QuestionnaireIcon className={classes.tabIcon} />
            <Typography variant="h4">{translations.questionnaire}</Typography>
            <Fade in={hasQuestionnaireErrors} unmountOnExit>
              <div className={classes.tabError}>
                <Tooltip
                  text={translations.questionnaireErrorsTooltip}
                  icon={ErrorIcon}
                />
              </div>
            </Fade>
          </div>
          <div
            className={classNames(classes.tabWrapper, classes.buttonWrapper, {
              [classes.selectedTabWrapper]: activeView === VIEW_PREVIEW,
            })}
            onClick={handleSetActiveView(VIEW_PREVIEW)}
          >
            <PreviewIcon className={classes.tabIcon} />
            <Typography variant="h4">{translations.preview}</Typography>
          </div>
        </div>
        {activeView === VIEW_QUESTIONS ? (
          renderQuestionsView()
        ) : (
          <div className={classes.previewWrapper}>
            <SurveyPreview
              translations={translations.surveyPreview}
              values={values}
              isQuestionnaire
            />
          </div>
        )}
      </div>
      <AlertDialog
        translations={translations.deleteQuestionDialog}
        isOpened={isDeleteDialogOpened}
        onClose={handleCloseDeleteQuestion}
        onConfirm={onDeleteQuestion}
        isWarning
      />
      <AlertDialog
        translations={translations.duplicateQuestionDialog}
        isOpened={isDuplicateDialogOpened}
        onClose={handleCloseDuplicateQuestion}
        onConfirm={onDuplicateQuestion}
      />
    </div>
  );
};

QuestionnaireQuestionsField.propTypes = {
  translations: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  questionnaire: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    questions: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  hasError: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onValidate: PropTypes.func,
  onRegisterFlush: PropTypes.func,
};

export default QuestionnaireQuestionsField;
